import { useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { Icon } from "./Icon";
import { ThemeToggle } from "./ThemeToggle";
import { useSession } from "@/contexts/SessionContext";
import { dashboardApi } from "@/api/endpoints/dashboard";
import { conversationsApi } from "@/api/endpoints/conversations";

interface TopbarProps {
  here: string;
  workspaceLabel: string;
  // Mobile drawer toggle (rendered <768px). Layouts that pass nothing simply
  // hide the button — the surrounding CSS still hides it on desktop.
  onToggleMobileSidebar?: () => void;
  onOpenSearch?: () => void;
}

export function Topbar({
  here,
  workspaceLabel,
  onToggleMobileSidebar,
  onOpenSearch,
}: TopbarProps): JSX.Element {
  const { me, logout, isAuthenticated } = useSession();
  const navigate = useNavigate();

  // ---- WP-D: global inbox unread badge ----------------------------------
  // Combines unread leads (from the dashboard summary card we already fetch
  // elsewhere — refetched cheaply here) with the unread conversation count.
  // Both queries are gated on auth so signed-out screens stay quiet.
  const summaryQuery = useQuery({
    queryKey: ["dashboard", "summary", "topbar"],
    queryFn: () => dashboardApi.summary(),
    enabled: isAuthenticated,
    staleTime: 60_000,
    retry: false,
  });
  const unreadConvsQuery = useQuery({
    queryKey: ["conversations", "unread", "topbar"],
    queryFn: () =>
      conversationsApi.list({ unread_only: true, page: 1, page_size: 1 }),
    enabled: isAuthenticated,
    staleTime: 30_000,
    retry: false,
  });
  const unreadLeads = summaryQuery.data?.unread_leads ?? 0;
  const unreadConvs = unreadConvsQuery.data?.total ?? 0;
  const unreadTotal = unreadLeads + unreadConvs;

  const initials = (me?.display_name ?? me?.email ?? "?")
    .split(/\s|@/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase())
    .join("");

  // Detect mac for the cmdk hint label. Falls back to ctrl on linux/win.
  const isMac =
    typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform);

  return (
    <div className="topbar" data-testid="topbar">
      {onToggleMobileSidebar ? (
        <button
          type="button"
          className="mobile-sidebar-toggle"
          aria-label="Open navigation"
          onClick={onToggleMobileSidebar}
          data-testid="mobile-sidebar-toggle"
        >
          <Icon name="menu" size={18} />
        </button>
      ) : null}
      <div className="crumbs">
        <span style={{ color: "var(--text-3)" }}>{workspaceLabel}</span>
        <span className="sep">/</span>
        <span className="here">{here}</span>
      </div>
      <div className="topbar-spacer" />
      {onOpenSearch ? (
        <button
          type="button"
          className="cmdk-trigger"
          onClick={onOpenSearch}
          aria-label="Open command palette"
          data-testid="cmdk-trigger"
        >
          <Icon name="search" size={12} />
          <span style={{ flex: 1, textAlign: "left" }}>Search…</span>
          <span className="cmdk-kbd">{isMac ? "⌘" : "Ctrl"}</span>
          <span className="cmdk-kbd">K</span>
        </button>
      ) : null}
      <div className="right">
        <ThemeToggle />
        <button
          className="icon-btn"
          aria-label={
            unreadTotal > 0
              ? `Notifications (${unreadTotal} unread)`
              : "Notifications"
          }
          onClick={() => navigate({ to: "/conversations" as "/dashboard" })}
          data-testid="topbar-inbox"
          style={{ position: "relative" }}
        >
          <Icon name="bell" />
          {unreadTotal > 0 ? (
            <span
              data-testid="topbar-inbox-badge"
              style={{
                position: "absolute",
                top: 2,
                right: 2,
                minWidth: 16,
                height: 16,
                padding: "0 4px",
                borderRadius: 8,
                background: "oklch(0.6 0.18 25)",
                color: "white",
                fontSize: 10,
                fontWeight: 600,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                lineHeight: 1,
              }}
            >
              {unreadTotal > 99 ? "99+" : unreadTotal}
            </span>
          ) : null}
        </button>
        <div className="me-pill">
          <span className="me-avatar">{initials}</span>
          <span style={{ fontSize: 12 }}>{me?.display_name ?? me?.email}</span>
        </div>
        <button
          className="btn btn-ghost btn-sm"
          onClick={async () => {
            await logout();
            navigate({ to: "/login" });
          }}
          data-testid="logout-button"
        >
          <Icon name="logout" /> Sign out
        </button>
      </div>
    </div>
  );
}
